
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { CheckCircle2, Info, Lock } from "lucide-react";
import { PromptSection } from "./PromptSection";
import { useState, useEffect } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface EditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tempPrompt: string;
  onPromptChange: (value: string) => void;
  onSave: () => void;
  agentType: string;
  promptSections: Array<{ title: string; description: string }>;
}

export const EditDialog = ({
  open,
  onOpenChange,
  tempPrompt,
  onPromptChange,
  onSave,
  agentType,
  promptSections,
}: EditDialogProps) => {
  const [activeTab, setActiveTab] = useState<string>("complete");
  const [sectionContents, setSectionContents] = useState<Record<string, string>>({});
  
  // Extract sections from the prompt when it changes or dialog opens
  useEffect(() => {
    if (!open) return;
    
    const extractedSections: Record<string, string> = {};
    
    // Simple extraction based on section titles
    promptSections.forEach(section => {
      const sectionTitle = section.title;
      const sectionRegex = new RegExp(`### ${sectionTitle}[\\s\\S]*?(?=### |$)`, "i");
      const match = tempPrompt.match(sectionRegex);
      
      if (match) {
        // Remove the title from the content and trim
        let content = match[0].replace(`### ${sectionTitle}`, "").trim();
        extractedSections[sectionTitle] = content;
      } else {
        extractedSections[sectionTitle] = "";
      }
    });
    
    setSectionContents(extractedSections);
  }, [tempPrompt, open, promptSections]);
  
  // Update the full prompt when section contents change
  const updateFullPrompt = () => {
    let newPrompt = "";
    
    promptSections.forEach(section => {
      const content = sectionContents[section.title] || "";
      if (content.trim()) {
        newPrompt += `### ${section.title}\n${content}\n\n`;
      }
    });
    
    onPromptChange(newPrompt.trim());
  };
  
  // Handle section content change
  const handleSectionChange = (sectionTitle: string, content: string) => {
    setSectionContents(prev => ({
      ...prev,
      [sectionTitle]: content
    }));
    
    // Debounce for better performance
    setTimeout(() => {
      updateFullPrompt();
    }, 300);
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Guided Prompt Editor</DialogTitle>
          <DialogDescription>
            Use this guided editor to refine the agent's system prompt
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-auto py-4">
          <Alert className="bg-primary/10 border-primary/20 mb-4">
            <Info className="h-4 w-4 text-primary" />
            <AlertDescription>
              You're editing the system prompt for the <span className="font-medium">{agentType}</span> agent. 
              Edit individual sections or the complete prompt.
            </AlertDescription>
          </Alert>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="w-full mb-4">
              {promptSections.map((section) => (
                <TabsTrigger key={section.title} value={section.title.toLowerCase().replace(/\s+/g, '-')}>
                  {section.title}
                </TabsTrigger>
              ))}
              <TabsTrigger value="complete">Complete Prompt</TabsTrigger>
            </TabsList>
            
            {promptSections.map((section) => (
              <TabsContent 
                key={section.title} 
                value={section.title.toLowerCase().replace(/\s+/g, '-')}
                className="space-y-4"
              >
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <Label className="text-lg font-medium">{section.title}</Label>
                    <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded-full">
                      Section
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">
                    {section.description}
                  </p>
                  <Textarea
                    value={sectionContents[section.title] || ""}
                    onChange={(e) => handleSectionChange(section.title, e.target.value)}
                    placeholder={`Enter details for ${section.title.toLowerCase()}...`}
                    className="min-h-[200px] font-mono text-sm"
                  />
                  <div className="text-xs text-muted-foreground mt-2">
                    Writing tips: Be clear and specific about the agent's {section.title.toLowerCase()}.
                  </div>
                </div>
              </TabsContent>
            ))}
            
            <TabsContent value="complete" className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="prompt-editor" className="text-lg font-medium">Complete Prompt</Label>
                <p className="text-sm text-muted-foreground mb-2">
                  Edit the full system prompt directly
                </p>
                <Textarea
                  id="prompt-editor"
                  value={tempPrompt}
                  onChange={(e) => onPromptChange(e.target.value)}
                  className="min-h-[300px] font-mono text-sm"
                />
              </div>
            </TabsContent>
          </Tabs>
        </div>

        <DialogFooter className="pt-4 border-t flex items-center justify-between w-full">
          <div className="text-sm text-muted-foreground">
            <CheckCircle2 className="h-4 w-4 inline mr-1 text-green-500" />
            Changes saved to editor
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={onSave}>
              Save Changes
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
